import { getLegacyUpgradeAction } from './legacyTransition';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

type JsonRecord = Record<string, unknown>;

export interface ProjectCreationEntitlementSnapshot {
    features?: JsonRecord;
    usage?: JsonRecord;
    derived?: JsonRecord;
    legacy_transition?: JsonRecord | null;
    billing_preview?: JsonRecord | null;
}

export const projectCreationSubscriptionMessage = msg({ id: 'projectCreation.subscriptionRequired', message: 'Your preview covers one project. Start a 14-day free trial to create another project.' });
export const projectCreationTrialLabel = msg({ id: 'projectCreation.startTrial', message: 'Start a 14-day free trial' });

function parseIntValue(value: unknown, defaultValue = 0): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return Math.trunc(value);
    }

    if (typeof value === 'boolean') {
        return value ? 1 : 0;
    }

    if (typeof value === 'string' && value.trim()) {
        const parsed = Number.parseInt(value, 10);
        return Number.isFinite(parsed) ? parsed : defaultValue;
    }

    return defaultValue;
}

function parseBooleanValue(value: unknown, defaultValue = false): boolean {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'number') {
        return value !== 0;
    }

    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['true', 't', '1', 'yes', 'y', 'on'].includes(normalized)) {
            return true;
        }
        if (['false', 'f', '0', 'no', 'n', 'off'].includes(normalized)) {
            return false;
        }
    }

    return defaultValue;
}

export function getProjectEntitlementSource(
    snapshot: ProjectCreationEntitlementSnapshot | null | undefined
): string {
    const source = typeof snapshot?.derived?.entitlement_source === 'string'
        ? snapshot.derived.entitlement_source
        : '';

    return source || 'free';
}

export function isProjectCreationLegacyGrace(
    snapshot: ProjectCreationEntitlementSnapshot | null | undefined
): boolean {
    return parseBooleanValue(snapshot?.derived?.legacy_entitlement_grace, false)
        || getProjectEntitlementSource(snapshot) === 'legacy_grace';
}

export function getProjectCreationMaxProjects(
    snapshot: ProjectCreationEntitlementSnapshot | null | undefined
): number {
    return parseIntValue(snapshot?.features?.max_projects, 0);
}

export function getProjectCreationActiveProjects(
    snapshot: ProjectCreationEntitlementSnapshot | null | undefined
): number {
    return parseIntValue(snapshot?.usage?.active_projects, 0);
}

export function canCreateProjectForSnapshot(
    snapshot: ProjectCreationEntitlementSnapshot | null | undefined
): boolean {
    if (!snapshot) {
        return true;
    }

    if (requiresSubscriptionForProjectCreation(snapshot)) {
        return false;
    }

    if (snapshot.derived && 'can_create_project' in snapshot.derived) {
        return parseBooleanValue(snapshot.derived.can_create_project, true);
    }

    const maxProjects = getProjectCreationMaxProjects(snapshot);
    return maxProjects > 0 && getProjectCreationActiveProjects(snapshot) < maxProjects;
}

export function requiresSubscriptionForProjectCreation(
    snapshot: ProjectCreationEntitlementSnapshot | null | undefined
): boolean {
    if (!snapshot || getProjectEntitlementSource(snapshot) === 'subscription'
        || isProjectCreationLegacyGrace(snapshot)
        || parseBooleanValue(snapshot.derived?.is_friend)) {
        return false;
    }
    const preview = snapshot.billing_preview;
    const transition = snapshot.legacy_transition;
    const formerLegacy = parseBooleanValue(transition?.is_former_legacy)
        || ['grace', 'expired_pending', 'enforced', 'converted'].includes(String(transition?.status));
    // Also supports the pre-migration RPC shape and structured DB rejections.
    return parseBooleanValue(snapshot.derived?.project_creation_requires_subscription)
        || Boolean(preview?.project_id || preview?.status)
        || parseBooleanValue(preview?.requires_checkout)
        || (!formerLegacy && parseBooleanValue(snapshot.usage?.has_non_system_project_history,
            getProjectCreationActiveProjects(snapshot) > 0));
}

export function shouldBlockProjectCreationForSnapshot(
    snapshot: ProjectCreationEntitlementSnapshot | null | undefined
): boolean {
    return Boolean(snapshot) && !canCreateProjectForSnapshot(snapshot);
}

export function buildProjectLimitMessageForSnapshot(
    snapshot: ProjectCreationEntitlementSnapshot | null | undefined
): MessageDescriptor {
    if (requiresSubscriptionForProjectCreation(snapshot)) {
        return projectCreationSubscriptionMessage;
    }
    const activeProjects = getProjectCreationActiveProjects(snapshot);
    const maxProjects = getProjectCreationMaxProjects(snapshot);
    const entitlementSource = getProjectEntitlementSource(snapshot);
    const hasFiniteLimit = maxProjects > 0 && maxProjects < 1000000;
    const transitionStatus = snapshot?.legacy_transition?.status;
    if (transitionStatus === 'expired_pending' || transitionStatus === 'enforced') {
        const upgradeAction = getLegacyUpgradeAction(snapshot);
        if (hasFiniteLimit) {
            return upgradeAction.hasOffer
                ? {
                    ...msg({ id: 'projectLimit.freeOfferCount', message: 'You are using {active} of {maximum} projects for your current plan. Your account is on the Free plan. View your early-user offer to add projects.' }),
                    values: { active: activeProjects, maximum: maxProjects },
                }
                : {
                    ...msg({ id: 'projectLimit.freePlansCount', message: 'You are using {active} of {maximum} projects for your current plan. Your account is on the Free plan. View plans to add projects.' }),
                    values: { active: activeProjects, maximum: maxProjects },
                };
        }
        return upgradeAction.hasOffer
            ? msg({ id: 'projectLimit.freeOffer', message: 'Your current plan does not allow another project right now. Your account is on the Free plan. View your early-user offer to add projects.' })
            : msg({ id: 'projectLimit.freePlans', message: 'Your current plan does not allow another project right now. Your account is on the Free plan. View plans to add projects.' });
    }

    if (isProjectCreationLegacyGrace(snapshot)) {
        return msg({ id: 'projectLimit.legacyGrace', message: 'Your existing projects stay available with legacy access. View plans to create another topic.' });
    }

    if (entitlementSource === 'free') {
        return hasFiniteLimit
            ? {
                ...msg({ id: 'projectLimit.freeCount', message: 'You are using {active} of {maximum} projects for your current plan. Upgrade to add another project, or delete an existing non-inbox project first.' }),
                values: { active: activeProjects, maximum: maxProjects },
            }
            : msg({ id: 'projectLimit.free', message: 'Your current plan does not allow another project right now. Upgrade to add another project, or delete an existing non-inbox project first.' });
    }

    return hasFiniteLimit
        ? {
            ...msg({ id: 'projectLimit.paidCount', message: 'You are using {active} of {maximum} projects for your current plan. Delete an existing non-inbox project or upgrade to add another.' }),
            values: { active: activeProjects, maximum: maxProjects },
        }
        : msg({ id: 'projectLimit.paid', message: 'Your current plan does not allow another project right now. Delete an existing non-inbox project or upgrade to add another.' });
}
